/**
 * Exercise version helpers.
 *
 * `topic.exercises[]` is version history, not a list of different exercises:
 * saving appends a new version and clears `isLatest` on the previous ones.
 * Readers must therefore take the entry flagged `isLatest`, not `exercises[0]`.
 *
 * Mirrors pickServedIndex() in server/migrations/lib/exercise-html.js - keep
 * both in step if the rule changes.
 */

export interface ExerciseVersion {
  type?: string;
  title?: string;
  content?: any;
  order?: number;
  version?: string;
  userId?: string;
  userEmail?: string;
  timeStamp?: string | Date;
  isLatest?: boolean;
  _id?: string;
}

/** Trailing number of a version string, e.g. "…-Version 3" -> 3. */
function versionNumber(entry?: ExerciseVersion): number {
  const m = /(\d+)\s*$/.exec(String(entry?.version ?? ''));
  return m ? Number(m[1]) : NaN;
}

/**
 * The exercise version that should be shown. Returns null when there is none.
 * Falls back to the highest version number, then the last entry, so documents
 * written before versioning existed still resolve to something sensible.
 */
export function pickLatestExercise(
  exercises?: ExerciseVersion[] | null
): ExerciseVersion | null {
  if (!Array.isArray(exercises) || exercises.length === 0) return null;

  const flagged = exercises.find((e) => e?.isLatest === true);
  if (flagged) return flagged;

  let best: ExerciseVersion | null = null;
  let bestV = -Infinity;
  for (const e of exercises) {
    const v = versionNumber(e);
    if (!Number.isNaN(v) && v > bestV) { bestV = v; best = e; }
  }
  if (best) return best;

  return exercises[exercises.length - 1] ?? null;
}

/**
 * Read the HTML out of an exercise version, whichever shape `content` uses.
 * Older records store a plain string; migrations wrote `{ html }` or
 * `{ htmlContent }`. All three are still present in live data.
 */
export function readExerciseHtml(entry?: ExerciseVersion | null): string | null {
  if (!entry) return null;
  const c = entry.content;
  if (typeof c === 'string') return c || null;
  if (c && typeof c === 'object') {
    if (typeof c.htmlContent === 'string') return c.htmlContent || null;
    if (typeof c.html === 'string') return c.html || null;
  }
  return null;
}

/** Latest version's HTML in one step. */
export function getLatestExerciseHtml(
  exercises?: ExerciseVersion[] | null
): string | null {
  return readExerciseHtml(pickLatestExercise(exercises));
}

/** Newest first, for a version-history list. */
export function sortVersionsNewestFirst(
  exercises?: ExerciseVersion[] | null
): ExerciseVersion[] {
  if (!Array.isArray(exercises)) return [];
  return [...exercises].sort((a, b) => {
    if (a?.isLatest && !b?.isLatest) return -1;
    if (b?.isLatest && !a?.isLatest) return 1;
    const va = versionNumber(a);
    const vb = versionNumber(b);
    if (!Number.isNaN(va) && !Number.isNaN(vb) && va !== vb) return vb - va;
    const ta = a?.timeStamp ? new Date(a.timeStamp).getTime() : 0;
    const tb = b?.timeStamp ? new Date(b.timeStamp).getTime() : 0;
    return tb - ta;
  });
}
